import { useEffect, useRef, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
)

// Chart ink — validated against a white card surface:
// series teal #1E7F88 (4.7:1), de-emphasis gray relies on direct labels
export const viz = {
  series: '#1E7F88',
  seriesSoft: 'rgba(30, 127, 136, 0.10)',
  deEmphasis: '#94A3B8',
  grid: '#EDF1F5',
  axisText: '#64748B',
  ink: '#242F40',
  surface: '#FFFFFF',
  good: '#00964C',
  warning: '#F28B1F',
  critical: '#E32525',
}

export const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#242F40',
      titleFont: { family: 'Poppins', weight: 600 },
      bodyFont: { family: 'Poppins' },
      padding: 10,
      cornerRadius: 5,
      displayColors: false,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { color: '#E2E8F0' },
      ticks: { color: viz.axisText, font: { family: 'Poppins', size: 11 } },
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: viz.grid, lineWidth: 1 },
      ticks: {
        precision: 0,
        color: viz.axisText,
        font: { family: 'Poppins', size: 11 },
        maxTicksLimit: 5,
      },
    },
  },
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

// Ease-out count-up for stat values; renders the final value immediately
// when the user prefers reduced motion
export const useCountUp = (target, duration = 900) => {
  const [value, setValue] = useState(0)
  const frame = useRef(null)

  useEffect(() => {
    const end = Number(target) || 0
    if (prefersReducedMotion()) {
      setValue(end)
      return undefined
    }
    const startTime = performance.now()
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(end * eased))
      if (progress < 1) frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame.current)
  }, [target, duration])

  return value
}
